import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Camera, SlidersHorizontal, Star, Inbox, CalendarOff, Users, Mail, BarChart3, CalendarDays, Contact, Settings, Package, Newspaper, MessageCircleQuestion, Briefcase, Eye, Bot, Sparkles, Car, Wrench } from "lucide-react";
import { LOGO } from "@/data/site";
import { API, auth, inputClass, labelClass } from "@/components/admin/shared";
import AdminPhotos from "@/components/admin/AdminPhotos";
import AdminBeforeAfter from "@/components/admin/AdminBeforeAfter";
import AdminReviews from "@/components/admin/AdminReviews";
import AdminQuotes from "@/components/admin/AdminQuotes";
import AdminQuestions from "@/components/admin/AdminQuestions";
import AdminRecruit from "@/components/admin/AdminRecruit";
import AdminVisitors from "@/components/admin/AdminVisitors";
import AdminAiChats from "@/components/admin/AdminAiChats";
import AdminHelp from "@/components/admin/AdminHelp";
import AdminMail from "@/components/admin/AdminMail";
import AdminClosedDays from "@/components/admin/AdminClosedDays";
import AdminTeam from "@/components/admin/AdminTeam";
import AdminPassword from "@/components/admin/AdminPassword";
import AdminStats from "@/components/admin/AdminStats";
import AdminCalendar from "@/components/admin/AdminCalendar";
import AdminCustomers from "@/components/admin/AdminCustomers";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminParts from "@/components/admin/AdminParts";
import AdminNews from "@/components/admin/AdminNews";
import AdminVehicles from "@/components/admin/AdminVehicles";
import AdminPartListings from "@/components/admin/AdminPartListings";
import { ForgotPassword, ResetPassword } from "@/components/admin/PasswordRecovery";
import { useLocation } from "react-router-dom";

const TABS = [
  { id: "quotes", label: "Demandes de devis", icon: Inbox, Comp: AdminQuotes },
  { id: "questions", label: "Questions", icon: MessageCircleQuestion, Comp: AdminQuestions },
  { id: "mail", label: "Boîte mail", icon: Mail, Comp: AdminMail },
  { id: "recruit", label: "Recrutement", icon: Briefcase, Comp: AdminRecruit },
  { id: "vehicles", label: "Véhicules", icon: Car, Comp: AdminVehicles },
  { id: "parts-market", label: "Pièces (vente / achat)", icon: Wrench, Comp: AdminPartListings },
  { id: "calendar", label: "Calendrier", icon: CalendarDays, Comp: AdminCalendar },
  { id: "customers", label: "Clients", icon: Contact, Comp: AdminCustomers },
  { id: "parts", label: "Stock atelier", icon: Package, Comp: AdminParts },
  { id: "stats", label: "Statistiques", icon: BarChart3, Comp: AdminStats },
  { id: "visitors", label: "Visiteurs", icon: Eye, Comp: AdminVisitors },
  { id: "ai-chats", label: "Chat IA", icon: Bot, Comp: AdminAiChats },
  { id: "closed-days", label: "Fermetures", icon: CalendarOff, Comp: AdminClosedDays },
  { id: "photos", label: "Photos", icon: Camera, Comp: AdminPhotos },
  { id: "before-after", label: "Avant / Après", icon: SlidersHorizontal, Comp: AdminBeforeAfter },
  { id: "reviews", label: "Avis clients", icon: Star, Comp: AdminReviews },
  { id: "news", label: "Actualités", icon: Newspaper, Comp: AdminNews },
  { id: "team", label: "Équipe", icon: Users, Comp: AdminTeam, ownerOnly: true },
  { id: "help", label: "Aide IA", icon: Sparkles, Comp: AdminHelp },
  { id: "settings", label: "Paramètres du site", icon: Settings, Comp: AdminSettings },
];

const readSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem("gal-admin-session")) || null;
  } catch {
    return null;
  }
};

const Admin = () => {
  const location = useLocation();
  const [forgot, setForgot] = useState(false);
  const [session, setSession] = useState(readSession);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("quotes");
  const [badges, setBadges] = useState({ quotes: 0, reviews: 0, questions: 0, recruit: 0, mail: 0, vehicles: 0 });
  const token = session?.token;
  const me = session?.user;

  useEffect(() => {
    if (!token) return;
    Promise.all([axios.get(`${API}/quotes`, auth(token)), axios.get(`${API}/reviews/all`, auth(token)), axios.get(`${API}/questions`, auth(token)), axios.get(`${API}/applications`, auth(token)), axios.get(`${API}/mail/status`, auth(token)).catch(() => ({ data: { unread: 0 } })), axios.get(`${API}/vehicles/leads`, auth(token)).catch(() => ({ data: [] })), axios.get(`${API}/vehicle-offers`, auth(token)).catch(() => ({ data: [] }))])
      .then(([q, r, qs, ap, ms, vl, vo]) =>
        setBadges({
          quotes: q.data.filter((x) => x.status === "new").length,
          reviews: r.data.filter((x) => x.status === "pending").length,
          questions: qs.data.filter((x) => x.status === "new").length,
          recruit: ap.data.filter((x) => x.status === "new").length,
          mail: ms.data.unread || 0,
          vehicles: vl.data.filter((x) => x.status === "new").length + vo.data.filter((x) => x.status === "new").length,
        })
      )
      .catch((err) => {
        if (err.response?.status === 401) logout();
      });
  }, [token, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, creds);
      sessionStorage.setItem("gal-admin-session", JSON.stringify(res.data));
      setSession(res.data);
      toast.success(`Bienvenue ${res.data.user.name || res.data.user.email} !`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("gal-admin-session");
    setSession(null);
  };

  if (location.pathname === "/admin/reset") return <ResetPassword />;
  if (!token && forgot) return <ForgotPassword onBack={() => setForgot(false)} />;

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-mint px-5 pt-20" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-md rounded-3xl border border-brand/10 bg-white p-10 shadow-2xl shadow-brand/10">
          <img src={LOGO} alt="GROUPE AUTO LUXE" className="mb-8 h-16 w-auto" />
          <h1 className="font-display text-2xl font-extrabold text-forest">Espace Entreprise</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
            Réservé aux responsables de Groupe Auto Luxe
          </p>
          <label className={`${labelClass} mt-8`}>Email</label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand/50" />
            <input type="email" required autoComplete="username" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} data-testid="admin-email-input" className={`${inputClass} pl-11`} placeholder="vous@groupeautoluxeci.com" />
          </div>
          <label className={`${labelClass} mt-5`}>Mot de passe</label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand/50" />
            <input type="password" required autoComplete="current-password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} data-testid="admin-password-input" className={`${inputClass} pl-11`} placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60">
            <Lock className="h-4 w-4" />
            {loading ? "Connexion..." : "Se connecter"}
          </button>
          <button type="button" onClick={() => setForgot(true)} data-testid="forgot-password-link" className="mt-5 w-full text-center font-mono text-[11px] uppercase tracking-widest text-brand hover:text-gold">
            Mot de passe oublié ?
          </button>
        </form>
      </main>
    );
  }

  const tabs = TABS.filter((t) => !t.ownerOnly || me?.role === "admin");
  const Active = (tabs.find((t) => t.id === tab) || tabs[0]).Comp;

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-5 pb-24 pt-36 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-forest">
            Espace <span className="text-gold">Entreprise</span>
          </h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand" data-testid="admin-current-user">
            Connecté : {me?.name || me?.email} · {me?.role === "admin" ? "Administrateur" : "Responsable"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <AdminPassword token={token} logout={logout} />
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-brand/30 px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-brand transition-colors hover:bg-mint">
            <LogOut className="h-4 w-4" />
            Déconnexion
          </button>
        </div>
      </div>

      <nav className="mt-10 flex flex-wrap gap-2 border-b border-brand/10 pb-4" data-testid="admin-tabs">
        {tabs.map(({ id, label, icon: Icon }) => {
          const badge = badges[id] || 0;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-all ${
                tab === id ? "bg-forest text-white shadow-lg shadow-forest/20" : "text-brand hover:bg-mint"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {badge > 0 && (
                <span className="ml-1 rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`admin-tab-${id}-badge`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="mt-10">
        <Active token={token} logout={logout} me={me} />
      </div>
    </main>
  );
};

export default Admin;
